import { inject, Injectable, signal, Signal } from '@angular/core';
import { Logger } from '@ngrx/data';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subscription } from 'rxjs';
import {
    catchError,
    debounceTime,
    defaultIfEmpty,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { distinctUntilChanged } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { entityChangeDataLoaded, EntityChangeSelector, LocalStorageDataService, NativeStorageService } from '../global/data';
import { throttleWithTrailing } from '../global/operators';
import { ForceSyncService } from '../global/sync/sync.force';
import { HttpClientWithInFlightCache } from '../global/httpClient';
import { ArmyBuilderConfig } from '../global/config';
import { SettingsService } from '../global/settings/service/settings.service';
import { selectRouter } from '../global/army-builder-store.module';
import { DataLibrary } from '../global/data-library';
import { forceEqualsIgnoringTimestamp, forceEqualsForCache } from '../global/utils';

import { FORCE_ACTIONS } from './state/actions';
import { selectForces } from './state/selectors';
import { removeSharedForce } from './shared-forces.actions';
import { Force, Platoon, PointsAdjustment, Unit, UnitOption, ValidationError } from './models';
import { ForceUtils } from './force.utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterService } from '../global/router.service';

@Injectable({ providedIn: 'root' })
export class ForceDataService extends LocalStorageDataService<Force> {
    name = 'forces';
}

let entityChangeSub: Subscription;

@Injectable({ providedIn: 'root' })
export abstract class ForceService {
    abstract dataLibrary: DataLibrary;
    abstract forceUtils: ForceUtils;

    protected forceSyncService = inject(ForceSyncService);
    protected httpClient = inject(HttpClientWithInFlightCache);
    protected config = inject(ArmyBuilderConfig);
    protected settingsService = inject(SettingsService);
    protected logger = inject(Logger);
    protected storage = inject(NativeStorageService);
    protected store = inject(Store);
    protected routerService = inject(RouterService); // Injecting this here ensures that the theme data loads based on route
    public forceLoaded = signal(false);

    ready$ = new BehaviorSubject(false);
    ready = toSignal(this.ready$);
    route$: Observable<any> = this.store.select(selectRouter).pipe(distinctUntilChanged());
    selectedPlatoonId$ = this.route$.pipe(
        map((r) => {
            return parseInt(r?.state.queryParams.platoonId || r?.state.params.platoonId);
        }),
        distinctUntilChanged(),
        shareReplay(1)
    );
    selectedPlatoonId = toSignal(this.selectedPlatoonId$);

    constructor() {
        // Track loading state separately from the force$ observable to avoid signal writes during computed evaluations
        combineLatest([this.forceId$, this.shared$])
            .pipe(
                tap(() => this.forceLoaded.set(false)),
                switchMap(([forceId]) =>
                    this.force$.pipe(
                        filter((f) => f?.id === forceId),
                        take(1),
                        tap(() => {
                            return this.forceLoaded.set(true);
                        })
                    )
                )
            )
            .subscribe();
    }

    postProcessCache: { [forceId: string]: Force } = {};

    // Stores the pre-processed input (store format + unitTemplate) for each force at the time it
    // was last processed. Cache validity is checked by comparing the incoming pre-processed force
    // against this snapshot rather than against postProcessCache. Comparing pre-vs-pre avoids false
    // mismatches caused by processForce adding computed-only fields (validationRuleId,
    // validationGroupId, theatreKey on units; faction/theatreName on platoons) that never go
    // back to the store, which made postProcessCache comparisons always fail.
    private inputSnapshotCache: { [forceId: string]: Force } = {};

    forceId$: Observable<any> = this.route$.pipe(
        map((r) => r?.state?.params.forceId),
        distinctUntilChanged(),
        shareReplay(1)
    );

    forces$: Observable<Force[]> = this.ready$.pipe(
        filter((r) => !!r),
        switchMap(() => this.settingsService.login$),
        switchMap(() => this.settingsService.loggedIn$),
        filter((l) => !!l),
        switchMap(() =>
            combineLatest([
                this.store.select(selectForces).pipe(
                    throttleWithTrailing(100), // Wait for batch operations to complete before preprocessing
                    distinctUntilChanged((prev, curr) => {
                        // Use custom comparison for each force that ignores updatedAt
                        // This prevents re-processing when only sync timestamps change
                        if (prev.length !== curr.length) {
                            console.log('[FORCE-SERVICE] Forces array length changed, will emit', {
                                prevLength: prev.length,
                                currLength: curr.length
                            });
                            return false;
                        }
                        // Use forceEqualsForCache (not forceEqualsIgnoringTimestamp) so that
                        // write-backs of computed output fields (cost, derived, validationErrors)
                        // do NOT cause forces$ to re-emit. Without this, every UPDATE_FORCES_BATCH
                        // or UPDATE_FORCE dispatch triggered a new forces$ pass, causing forces$ to
                        // fire 4 times and reprocess all forces on each pass for a single user action.
                        const allEqual = prev.every((pf, i) => forceEqualsForCache(pf, curr[i]));
                        if (!allEqual) {
                            console.log('[FORCE-SERVICE] Force data changed, will emit');
                            // Find which force changed
                            prev.forEach((pf, i) => {
                                if (!forceEqualsForCache(pf, curr[i])) {
                                    console.log('[FORCE-SERVICE] Changed force:', {
                                        id: pf?.id || curr[i]?.id,
                                        prevName: pf?.name,
                                        currName: curr[i]?.name
                                    });
                                }
                            });
                        }
                        return allEqual;
                    })
                ),
                this.dataLibrary.unitTemplates$.pipe(
                    startWith([]) // Emit empty array immediately, then real templates when loaded
                ),
                this.forceId$
            ])
        ),
        map(([forces, unitTemplates, selectedForceId]): Force[] => {
            const gameId = this.gameId;
            const gameForcesCount = forces.filter((f) => f.gameId === gameId).length;

            // Skip preprocessing if unit templates haven't loaded yet
            // But ensure forces have minimal structure for display
            if (unitTemplates.length === 0) {
                return forces
                    .filter((f) => f.gameId === gameId)
                    .map((force) => ({
                        ...force,
                        validationErrors: force.validationErrors || [],
                        selected: force.id === selectedForceId
                    }));
            }

            const result = forces
                .filter((f) => f.gameId === gameId)
                .map((force) => this.forceUtils.preProcessForce(force, unitTemplates, selectedForceId));

            return result;
        }),
        switchMap(
            (forces): Observable<Force[]> =>
                this.forceSyncService.entityChangeCache$.pipe(
                    throttleWithTrailing(50), // Throttle with immediate first emit, then trailing after quieting
                    switchMap(async (entityChangeCache) => {
                        const perfStart = performance.now();
                        console.log('[FORCE-PERF] Force processing started');

                        // Process forces sequentially to avoid CPU lockup
                        const processedForces: any[] = [];
                        const batchUpdates: Array<Partial<Force> & { id: string }> = [];

                        let processedCount = 0;
                        let lastYieldTime = performance.now();

                        for (let i = 0; i < forces.length; i++) {
                            const f = forces[i];

                            if (this.gameId !== f.gameId) {
                                console.warn('Force gameId does not match ForceService gameId', {
                                    forceGameId: f.gameId,
                                    serviceGameId: this.gameId,
                                    forceId: f.id
                                });
                                continue;
                            }

                            const isInCache = Object.keys(this.postProcessCache).includes(f.id);

                            // Compare the incoming pre-processed force against the pre-processed snapshot
                            // saved when we last processed this force. Pre-vs-pre comparison avoids
                            // false mismatches caused by processForce adding computed-only fields
                            // (validationRuleId, theatreKey, etc.) that never get written back to the
                            // store, which would make a pre-vs-postProcess comparison always fail.
                            // When entityChangeCache$ fires with the same forces$ closure (e.g. the
                            // trailing edge of throttleWithTrailing, or a cost dirty-mark) the same
                            // Force reference is used, so forceEqualsForCache returns true immediately.
                            const cacheMatchesIncoming = isInCache && forceEqualsForCache(f, this.inputSnapshotCache[f.id]);

                            // Use cache whenever inputs match, regardless of dirty state.
                            // 'Dirty' indicates that the force needs syncing to the server — it does
                            // NOT mean the inputs have changed or that reprocessing is needed.
                            // cacheMatchesIncoming already detects real input changes (selectedOptions,
                            // experience, variants, etc.) and will be false when reprocessing is required.
                            if (isInCache && cacheMatchesIncoming) {
                                processedForces.push({
                                    ...this.postProcessCache[f.id],
                                    selected: f.selected
                                });
                                continue;
                            }

                            // Process the force if:
                            //   - it has no cost yet, OR
                            //   - its derived data needs migration, OR
                            //   - it was previously cached but the incoming data has changed
                            //     (e.g. a unit was added/removed — cache exists but no longer matches)
                            // AND unit templates must be attached (or the force is empty) — otherwise
                            // we can't calculate costs yet and must wait for templates to load.
                            //
                            // Note: we only use the cache-mismatch path when isInCache is true.
                            // If the force is not in cache yet (initial load), the stored cost is
                            // assumed correct and we fall through to the else branch to avoid
                            // reprocessing every force on startup.
                            const updateDerivedData = this.forceUtils.shouldUpdateDerivedData(f);
                            const cacheStale = isInCache && !cacheMatchesIncoming;
                            if ((!f.cost || updateDerivedData || cacheStale) && (f.units?.length === 0 || f.units?.[0]?.unitTemplate)) {
                                processedCount++;

                                this.inputSnapshotCache[f.id] = f;
                                let processedForce = await this.forceUtils.processForce(f);
                                let postProcessedForce = this.forceUtils.postProcessForce(processedForce);
                                this.postProcessCache[f.id] = postProcessedForce;

                                // Collect updates if the cost changed OR if derived migration was needed.
                                // When migrating, also bump appVersion so subsequent loads skip migration.
                                //
                                // validationErrors always rides along whenever we push an update here.
                                // postProcessedForce was just computed above, so it is never stale — the
                                // bug this fixes was that appVersion (and cost) could be persisted from a
                                // fresh processForce()/postProcessForce() pass while the validationErrors
                                // that same pass computed were silently dropped, leaving a force whose
                                // appVersion says "validated on the current release" with validationErrors
                                // that could be arbitrarily older. That gap is exactly what makes stored
                                // validationErrors unsafe to treat as ground truth for regression testing
                                // purely by filtering on appVersion.
                                const oldCost = f.cost ?? 0;
                                const newCost = postProcessedForce.cost ?? 0;
                                if (oldCost !== newCost || updateDerivedData) {
                                    batchUpdates.push({
                                        id: postProcessedForce.id,
                                        cost: postProcessedForce.cost,
                                        validationErrors: postProcessedForce.validationErrors,
                                        ...(updateDerivedData
                                            ? { derived: postProcessedForce.derived, appVersion: this.config.version }
                                            : {})
                                    });
                                }

                                processedForces.push(postProcessedForce);

                                // PERFORMANCE OPTIMIZATION: Yield to browser to prevent UI lockup
                                // Time-based yielding (every 1000ms) instead of count-based (every N forces)
                                // provides better balance between throughput and responsiveness.
                                // Testing showed: count-based yielding (every 2 forces) = 4,790ms overhead
                                // vs time-based yielding (every 1000ms) = 1,971ms overhead for 99 forces.
                                const currentTime = performance.now();
                                const timeSinceLastYield = currentTime - lastYieldTime;
                                if (timeSinceLastYield > 1000) {
                                    await new Promise((resolve) => setTimeout(resolve, 0));
                                    lastYieldTime = performance.now();
                                }
                            } else {
                                // Return header info only
                                processedForces.push(f);
                            }
                        }

                        // Batch dispatch all cost updates in a single action
                        if (batchUpdates.length > 0) {
                            this.store.dispatch(
                                FORCE_ACTIONS.UPDATE_FORCES_BATCH({
                                    forces: batchUpdates
                                })
                            );
                        }

                        const perfEnd = performance.now();
                        const perfDuration = perfEnd - perfStart;
                        console.log(
                            `[FORCE-PERF] Force processing completed in ${perfDuration.toFixed(1)}ms - Processed ${processedCount} forces, Cached ${processedForces.length - processedCount}`
                        );

                        return processedForces;
                    })
                )
        ),
        defaultIfEmpty([]),
        shareReplay(1)
    ) as Observable<Force[]>;

    unitId$: Observable<any> = this.route$.pipe(
        map((r) => r?.state.params.unitId),
        distinctUntilChanged()
    );

    shared$ = this.route$.pipe(
        map((r) => !!r?.state?.data?.shared),
        distinctUntilChanged(),
        shareReplay(1)
    );

    force$: Observable<Force> = this.ready$.pipe(
        filter((r) => !!r),
        switchMap(() => combineLatest([this.forceId$, this.shared$])),
        switchMap(([forceId, shared]) => {
            if (!this.dataLibrary) {
                const msg = 'Data Library not provided';
                console.error(msg);
                throw new Error(msg);
            }

            if (!this.dataLibrary.unitTemplates$) {
                const msg = 'Unit Templates not provided';
                console.error(msg);
                throw new Error(msg);
            }
            return this.dataLibrary.unitTemplates$.pipe(
                map((ut) => {
                    return [forceId, shared, ut];
                })
            );
        }),
        switchMap(([forceId, shared, unitTemplates]) => {
            if (shared) {
                return this.forceUtils.getForceFromServer(forceId).pipe(
                    map((f) => this.forceUtils.preProcessForce(f, unitTemplates, f.id)),
                    switchMap((f) => this.forceUtils.processForce(f)),
                    map((f) => this.forceUtils.postProcessForce(f)),
                    catchError((err) => {
                        console.error(`Force ${forceId} not found on server`);
                        this.forceLoaded.set(true);
                        return of(null); // This catches the error thrown by the shared force not being found
                    })
                );
            }

            // Watch the raw store directly for the active force only, bypassing
            // forces$ (which processes ALL forces on every store change).
            //
            // forceEqualsForCache ignores cost/updatedAt/selected at the force level,
            // so when this pipeline writes back a cost update (UPDATE_FORCE), the
            // resulting store emission is filtered out — processForce runs exactly
            // once per meaningful user action with no re-entrant cycle.
            //
            // forces$ continues to run independently for the force list page; it
            // will find the result already in postProcessCache (written here) and
            // skip reprocessing via its cacheMatchesIncoming check.
            return this.store.select(selectForces).pipe(
                map((forces) => forces.find((f) => f.id === forceId && f.gameId === this.gameId)),
                filter((f) => !!f),
                distinctUntilChanged(forceEqualsForCache),
                switchMap(async (force) => {
                    if (unitTemplates.length === 0) {
                        // Unit templates haven't loaded yet
                        return null;
                    }

                    const preprocessed = this.forceUtils.preProcessForce(force, unitTemplates, forceId);
                    if (preprocessed.units?.length === 0 && (force.units?.length ?? 0) > 0) {
                        // No units survived template attachment — library not yet ready
                        return null;
                    }

                    const processed = await this.forceUtils.processForce(preprocessed);
                    const postProcessed = this.forceUtils.postProcessForce(processed);

                    this.postProcessCache[force.id] = postProcessed;

                    // If cost has changed, persist it to the store in order to trigger a sync
                    // to the server - this ensures that if costs change, the force headers will
                    // be updated accordingly.
                    // Use a partial UPDATE_FORCE (cost only) instead of a full upsert to avoid
                    // overwriting concurrent selectedOptions changes from rapid user actions.
                    // A full upsert replaces the entire force in the store, so an intermediate
                    // processForce result can clobber option additions that occurred during processing.

                    if (force.cost !== postProcessed.cost) {
                        this.update({ id: postProcessed.id, cost: postProcessed.cost });
                    }

                    return postProcessed;
                })
            );
        }),
        filter((f) => !!f),
        distinctUntilChanged((prev, curr) => {
            // Use custom comparison that ignores updatedAt timestamp
            // This prevents page jumping and re-renders when only the sync timestamp changes
            const isEqual = forceEqualsIgnoringTimestamp(prev, curr);
            return isEqual;
        }),
        shareReplay(1)
    );

    units$: Observable<any> = this.force$.pipe(
        filter((f) => !!f),
        map((force) => {
            let validationErrors: { [unitId: string]: ValidationError[] } = force.validationErrors.reduce((agg, ve) => {
                if (ve.unitId) {
                    if (!agg[ve.unitId]) {
                        agg[ve.unitId] = [];
                    }

                    agg[ve.unitId].push(ve);
                }

                return agg;
            }, {});

            return force.units.map((u) => ({
                ...u,
                validationErrors: validationErrors[u.id] ?? []
            }));
        }),
        distinctUntilChanged(),
        defaultIfEmpty([])
    );

    unit$: Observable<any> = combineLatest([this.units$, this.unitId$]).pipe(
        map((results) => {
            const units: Unit[] = results[0];
            const unitId: string = results[1];

            const unit = units.find((u) => u.id === unitId);
            return unit;
        }),
        filter((f) => !!f),
        distinctUntilChanged()
    );

    options$: Observable<any> = this.unit$.pipe(
        map((unit) => unit.options),
        distinctUntilChanged()
    );

    abstract gameId: string;
    gameId$: Observable<string> = this.store.select((s: any) => s?.router?.state?.data?.gameId).pipe(distinctUntilChanged());
    routeGameId: Signal<string> = toSignal(this.gameId$);

    // These signals cause the app to subscribe to the source observables
    // before the datalibrary is loaded.  As a result, if you need the
    // datalibrary in a derived observable, be sure to use the ready$
    // observable, combined with a check to see if the datalibrary has
    // been instantiated yet (see the force$ observable for an example)
    forceId: Signal<number> = toSignal(this.forceId$);
    unitId: Signal<number> = toSignal(this.unitId$);
    forces: Signal<Force[]> = toSignal(this.forces$);
    force: Signal<Force> = toSignal(this.force$);
    units: Signal<Unit[]> = toSignal(this.units$);
    unit: Signal<Unit> = toSignal(this.unit$);
    options: Signal<UnitOption[]> = toSignal(this.options$);
    shared: Signal<boolean> = toSignal(this.shared$);

    copyForce(force: Force) {
        const newForce = {
            ...force,
            id: null,
            shareViaURL: false,
            sharedWith: [],
            name: force.name + ' Copy'
        };
        return this.add(newForce);
    }

    saveSharedForce(force: Force) {
        return this.copyForce({ ...force });
    }

    removeShare(forceId: string, gameId: string) {
        const url = `${this.config.apiBaseUrl}/userData/forces/shared/${gameId}/${forceId}`;
        return this.httpClient.delete(url).subscribe(() => {
            this.store.dispatch(removeSharedForce({ forceId, gameId }));
        });
    }

    dismissAlert(force: Force, alertId: string) {
        const url = `${this.config.apiBaseUrl}/userData/forces/${force.id}/alerts/${alertId}`;
        return this.httpClient.delete(url).pipe(
            tap(() => {
                const updatedForce: Force = {
                    ...force,
                    alerts: (force.alerts || []).filter((a) => a.id !== alertId)
                };
                this.store.dispatch(FORCE_ACTIONS.UPDATE_FORCE({ force: updatedForce }));
            })
        );
    }

    protected initGame() {}
    init() {
        // N.B. this relies on the ForceService NOT being a singleton, as it allows each
        // module to load its own translations and THEN ready up the service, which
        // leads to each force being processed by that module's ForceUtils, where the
        // translations are needed.

        this.initGame();
        this.ready$.next(true);
        this.store
            .select(selectForces)
            .pipe(debounceTime(1000))
            .subscribe((forces) => {
                this.storage.setItem('forces', forces);
            });
    }

    /**
     * Immediately persist the current state to storage.
     * Call this after critical sync operations (like downloading a force after conflict resolution)
     * to ensure the data is saved before the user can close/refresh the app.
     */
    async persistImmediately() {
        const [forces, entityChangeState] = await Promise.all([
            firstValueFrom(this.store.select(selectForces)),
            firstValueFrom(this.store.select(EntityChangeSelector))
        ]);

        console.log('[ForceService] Persisting immediately:', {
            forceCount: forces.length,
            entityChangeState
        });

        await Promise.all([this.storage.setItem('forces', forces), this.storage.setItem('EntityChangeSelector', entityChangeState)]);
    }

    add(force: Force) {
        this.logger.log('ForceService.add');
        const newId = force.id || ('' + Date.now() + Math.floor(Math.random() * 1000)).toString();

        force = { ...force, id: newId };

        this.store.dispatch(FORCE_ACTIONS.ADD_FORCE({ force }));
        return of(force);
    }

    addUnit(unit: Unit, force: Force) {
        this.store.dispatch(FORCE_ACTIONS.ADD_UNIT({ forceId: force.id, unit }));
    }

    deleteUnit(force: Force, unit: Unit) {
        return this.update({
            id: force.id,
            units: force.units.filter((u) => u.id !== unit.id)
        });
    }

    async loadFromStorage() {
        const entityChangeData = await this.storage.getItem('EntityChangeSelector', {});
        const deletedForceIds = Object.entries(entityChangeData?.forces || {})
            .filter((x) => x[1] === 'Deleted')
            .map((x) => x[0]);

        this.store.dispatch(entityChangeDataLoaded(entityChangeData));

        if (!entityChangeSub) {
            console.log('Setting up entityChangeSub');
            entityChangeSub = this.store
                .select(EntityChangeSelector)
                .pipe(debounceTime(500))
                .subscribe((state) => {
                    console.log('EntityChangeSelector', state);
                    this.storage.setItem('EntityChangeSelector', state);
                });
        }

        const entities = await this.storage.getItem('forces', []);
        if (entities.length > 0) {
            // Don't load if the array is empty, since that is the default anyway,
            // and because the sync service could be running, storage may be
            // temporarily empty
            this.store.dispatch(FORCE_ACTIONS.LOAD_ALL_FORCES({ forces: entities.filter((f) => !deletedForceIds.includes(f.id)) }));
        }
    }

    updateUnit(force: Force, unit: Unit) {
        this.logger.log('ForceService.updateUnit');

        const updatedForce: Force = {
            ...force,
            units: force.units.map((u) => {
                if (u.id === unit.id) {
                    return unit;
                }
                return u;
            })
        };

        return this.update(updatedForce);
    }

    update(force: Partial<Force> & { id: string }) {
        this.logger.log('ForceService.update', force.id);

        this.store.dispatch(FORCE_ACTIONS.UPDATE_FORCE({ force }));
    }

    upsert(force: Force) {
        this.logger.log('ForceService.upsert');
        if (!force.id) {
            const newId = force.id || ('' + Date.now() + Math.floor(Math.random() * 1000)).toString();
            force = {
                ...force,
                id: newId
            };
        }
        this.store.dispatch(FORCE_ACTIONS.UPSERT_FORCE({ force }));
        return force;
    }

    delete(forceId: string) {
        this.logger.log('ForceService.delete');
        const deletedForceId$ = this.forces$.pipe(
            take(1),
            map((forces) => {
                const force = forces.find((f) => f.id === forceId);
                const updatedAt = force.updatedAt;

                this.store.dispatch(FORCE_ACTIONS.DELETE_FORCE({ id: forceId, updatedAt }));

                return forceId;
            })
        );

        deletedForceId$.subscribe((forceId) => {
            console.log('Force deleted: ' + forceId);
        });

        return deletedForceId$;
    }

    addPointsAdjustmentToUnit(forceId: string, unitId: string, pointsAdjustment: PointsAdjustment) {
        this.store.dispatch(FORCE_ACTIONS.ADD_POINTS_ADJUSTMENT({ forceId, unitId, pointsAdjustment }));
    }

    removePointsAdjustmentFromUnit(forceId: string, unitId: string, index: number) {
        this.store.dispatch(FORCE_ACTIONS.REMOVE_POINTS_ADJUSTMENT({ forceId, unitId, index }));
    }

    deletePlatoon(forceId, platoonId) {
        const action = FORCE_ACTIONS.DELETE_PLATOON({ forceId, platoonId });
        this.store.dispatch(action);
        return action;
    }

    duplicatePlatoon(forceId, platoonId) {
        this.store.dispatch(FORCE_ACTIONS.DUPLICATE_PLATOON({ forceId, platoonId }));
    }

    addPlatoon(force: Force, platoon: Platoon) {
        this.store.dispatch(FORCE_ACTIONS.ADD_PLATOON({ forceId: force.id, platoon }));
    }

    addUnitOption(forceId: string, unitId: string, optionId: number) {
        this.store.dispatch(FORCE_ACTIONS.ADD_UNIT_OPTION({ forceId, unitId, optionId }));
    }

    removeUnitOption(forceId: string, unitId: string, optionId: number) {
        this.store.dispatch(FORCE_ACTIONS.REMOVE_UNIT_OPTION({ forceId, unitId, optionId }));
    }

    /**
     * Invalidates the postProcess cache for a specific force.
     * This should be called when force data is updated from an external source
     * (e.g., downloaded from server) to ensure the UI reflects the new data.
     */
    invalidateCache(forceId: string) {
        delete this.postProcessCache[forceId];
        delete this.inputSnapshotCache[forceId];
    }
}
